import PropTypes from 'prop-types';
import React, { useState, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { DialogTitle, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Formik } from 'formik';
import { RepliconDialog } from '~/modules/common/components';
import { useGetScheduleHoursForUserAndDateRange } from '../../common/hooks/useGetScheduleHoursForUserAndDateRange';
import AllocateResourceDialogContent from './AllocateResourceDialogContent';
import AllocateResourceDialogFooter from './AllocateResourceDialogFooter';
import useAllocateResourcePopupHandler from './hooks/useAllocateResourcePopupHandler';
import { useAllocateResourceDialogSettings } from './hooks/useAllocateResourceDialogSettings';
import AllocateResourceDialogLoadingState from './AllocateResourceDialogLoadingState';
import { FIELDS } from './hooks/useOnChangeHandlers';

const useDilaogTitleStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2, 2, 2, 2)
  }
}));

const AllocateResourceDialogTitleId = 'allocate-resources-dialog-title';

const AllocateResourceDialog = ({
  defaultUser,
  fetchProjectDetails,
  open,
  onClose,
  handleAddResourceAllocation,
  scale,
  showAddUser = false,
  showAddProject = false,
  isSaving = false,
  setExistingResourceAllocation,
  existingResourceAllocation,
  defaultDates,
  project,
  isSmartFitOpen = false
}) => {
  const dialogTitleClasses = useDilaogTitleStyles();
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [focusField, setFocusField] = useState(
    showAddProject ? FIELDS.PROJECT_FIELD : FIELDS.STARTDATE_FIELD
  );
  const checkIsDirty = useCallback(() => isFormDirty, [isFormDirty]);

  const onSubmit = useCallback(
    values => {
      setFocusField(FIELDS.SUBMIT_BUTTON);
      handleAddResourceAllocation(values);
    },
    [handleAddResourceAllocation]
  );

  const {
    isLoading: isKeyValueSettingsLoading,
    dialogSettings
  } = useAllocateResourceDialogSettings();

  const {
    scheduledHours: totalScheduledHours,
    scheduleRules,
    loading: isLoadingScheduleRules
  } = useGetScheduleHoursForUserAndDateRange({
    userUri: defaultUser?.uri,
    dateRange: defaultDates,
    skip: !defaultUser
  });

  const { initialValues, validationSchema } = useAllocateResourcePopupHandler({
    scale,
    defaultDates,
    user: defaultUser,
    projectRole: null,
    totalScheduledHours,
    scheduleRules,
    project,
    savedShowAdvancedSettings: dialogSettings?.showAdvancedSettings,
    savedSelectedAllocationPeriod: dialogSettings?.selectedAllocationPeriod,
    savedDisplayUnit: dialogSettings?.displayUnit
  });

  return (
    <RepliconDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      data-qe-id="AllocateResourcesDialog"
      aria-labelledby={AllocateResourceDialogTitleId}
      checkIsDirty={checkIsDirty}
      enableUnsavedChangesWarning
      trapFocusWithDatePicker
    >
      <DialogTitle classes={dialogTitleClasses} disableTypography>
        <Typography variant="h6" id={AllocateResourceDialogTitleId}>
          <FormattedMessage id="allocateResourcesContent.allocateResource" />
        </Typography>
      </DialogTitle>
      {isKeyValueSettingsLoading || isLoadingScheduleRules ? (
        <AllocateResourceDialogLoadingState
          open={open}
          onClose={onClose}
          showAddProject={showAddProject}
          showAddUser={showAddUser}
        />
      ) : (
        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          <>
            <AllocateResourceDialogContent
              defaultUser={defaultUser}
              fetchProjectDetails={fetchProjectDetails}
              totalScheduledHours={totalScheduledHours}
              scale={scale}
              setExistingResourceAllocation={setExistingResourceAllocation}
              existingResourceAllocation={existingResourceAllocation}
              showAddUser={showAddUser}
              showAddProject={showAddProject}
              setIsFormDirty={setIsFormDirty}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              focusField={focusField}
              setFocusField={setFocusField}
            />
            <AllocateResourceDialogFooter
              onClose={onClose}
              isSaving={isSaving}
              isLoading={isLoading}
              isSmartFitOpen={isSmartFitOpen}
              focusField={focusField}
            />
          </>
        </Formik>
      )}
    </RepliconDialog>
  );
};

AllocateResourceDialog.propTypes = {
  defaultUser: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  handleAddResourceAllocation: PropTypes.func,
  scale: PropTypes.string,
  showAddUser: PropTypes.bool,
  showAddProject: PropTypes.bool,
  isSaving: PropTypes.bool,
  setExistingResourceAllocation: PropTypes.func,
  existingResourceAllocation: PropTypes.object,
  fetchProjectDetails: PropTypes.func,
  defaultDates: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string
  }),
  project: PropTypes.object,
  isSmartFitOpen: PropTypes.bool
};

export default AllocateResourceDialog;
